import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import DoubleTranslatedCustomRichText from '@/components/CustomRichText/DoubleTranslatedCustomRichText';import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import InfoMsg from '@/components/UI/InfoMsg/InfoMsg';
import Spinner from '@/components/UI/Spinner/Spinner';
import * as Constants from '@/helpers/constants';
import { getEditorId, removeParagraphTags, tabLowerCase } from '@/helpers/utils';
import useToastContext from '@/hooks/useToastContext';
import { setHelpSoftware } from '@/redux/Help/help.action';
import { getHelpSoftware } from '@/redux/Help/help.reducer';
import administrationServices from '@/services/administrationServices';
import { ReduxHelp } from '@/types';
import styles from '../HelpText.module.scss';

interface SoftwareHelpTextProps {
  helpSoftware: ReduxHelp;
  setSoftware: (help: ReduxHelp) => void;
}

const HELP_SUFFIX_TAB = 'TAB';
const HELP_SUFFIX_REQUIREMENTS = 'REQUIREMENTS';
const HELP_SUFFIX_CODING_REMOTE_SOFTWARE_UPDATE = 'CODING_REMOTE_SOFTWARE_UPDATE';
const HELP_SUFFIX_WITH_CODING = 'WITH_CODING';
const HELP_SUFFIX_FLASHING_INFORMATION = 'FLASHING_INFORMATION';
const HELP_SUFFIX_WITH_FLASHING = 'WITH_FLASHING';
const HELP_SUFFIX_FLASHING_MBOS = 'FLASHING_MBOS';
const HELP_SUFFIX_CODING_MBOS = 'CODING_MBOS';
const HELP_SUFFIX_FLASHING_REMOTE_SOFTWARE_UPDATE = 'FLASHING_REMOTE_SOFTWARE_UPDATE';

const ID_GENERAL_SOFTWARE = 'general-software';
const ID_REQUIREMENTS_SOFTWARE = 'requirements-software';
const ID_CODING_INFORMATION_SOFTWARE = 'coding-information-software';
const ID_CODING_ECU_SOFTWARE = 'coding-ecu-software';
const ID_FLASHING_SOFTWARE = 'flashing-software';
const ID_FLASHING_ECU_SOFTWARE = 'flashing-ecu-software';
const ID_FLASHING_MBOS_SOFTWARE = 'flashing-mbos-software';
const ID_CODING_MBOS_SOFTWARE = 'coding-mbos-software';
const ID_FLASHING_ECU_SOFTWARE_OTA = 'flashing-ecu-software-ota';

const SoftwareHelpText: React.FC<SoftwareHelpTextProps> = (props) => {
  const { t } = useTranslation();
  const tab = tabLowerCase(window.location.pathname.split('/')[4]);
  const [originalValues, setOriginalValues] = useState({});
  const { helpSoftware, setSoftware } = props;
  const isLoading = helpSoftware && !helpSoftware.AFFECTED_SOFTWARE_TAB;
  const { addToast } = useToastContext();

  useEffect(() => {
    administrationServices.getHelp(tab)
      .then((data) => {
        const values = helpSoftware && helpSoftware?.modified ? helpSoftware : data;
        setSoftware(values);
        setOriginalValues(values);
      })
      .catch((error) => addToast(error.message, 'ERROR'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWysiwygChange = (lang, labelSuffix, data) => {
    const label = `AFFECTED_SOFTWARE_${labelSuffix}`;
    const propValues = { ...helpSoftware[label] };
    const isModified = removeParagraphTags(data) !== removeParagraphTags(originalValues[label]?.[lang] || '');
    const newState = {
      ...helpSoftware,
      [label]: {
        ...propValues,
        [lang]: data,
      },
      modified: isModified,
    };

    if (isModified) {
      setSoftware(newState as unknown as ReduxHelp);
      setOriginalValues(newState);
    }
  };

  return (
    <>
      {isLoading && <Spinner isVisible isMask />}
      {!isLoading && (
        <>
          <InfoMsg msg={t('administration.helpText.info')} />
          <AccordionPanel title={t('administration.helpText.generalText')}>
            <DoubleTranslatedCustomRichText
              id={ID_GENERAL_SOFTWARE}
              title={t('helptext')}
              initialValue={helpSoftware?.AFFECTED_SOFTWARE_TAB}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, HELP_SUFFIX_TAB, newValue)}
            />
          </AccordionPanel>
          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('software.requirements')}`}>
            <DoubleTranslatedCustomRichText
              id={ID_REQUIREMENTS_SOFTWARE}
              title={t('helptext')}
              initialValue={helpSoftware?.AFFECTED_SOFTWARE_REQUIREMENTS}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, HELP_SUFFIX_REQUIREMENTS, newValue)}
            />
          </AccordionPanel>
          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('software.codingInformation')}`}>
            <DoubleTranslatedCustomRichText
              id={ID_CODING_INFORMATION_SOFTWARE}
              title={t('helptext')}
              initialValue={helpSoftware?.AFFECTED_SOFTWARE_CODING_REMOTE_SOFTWARE_UPDATE}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, HELP_SUFFIX_CODING_REMOTE_SOFTWARE_UPDATE, newValue)}
            />
          </AccordionPanel>
          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('software.reworkCodingECUsHelp')}`}>
            <DoubleTranslatedCustomRichText
              id={ID_CODING_ECU_SOFTWARE}
              title={t('helptext')}
              initialValue={helpSoftware?.AFFECTED_SOFTWARE_WITH_CODING}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, HELP_SUFFIX_WITH_CODING, newValue)}
            />
          </AccordionPanel>
          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('software.flashingInformation')}`}>
            <DoubleTranslatedCustomRichText
              id={ID_FLASHING_SOFTWARE}
              title={t('helptext')}
              initialValue={helpSoftware?.AFFECTED_SOFTWARE_FLASHING_INFORMATION}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, HELP_SUFFIX_FLASHING_INFORMATION, newValue)}
            />
          </AccordionPanel>
          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('software.reworkFlashingECUsHelp')}`}>
            <DoubleTranslatedCustomRichText
              id={ID_FLASHING_ECU_SOFTWARE}
              title={t('helptext')}
              initialValue={helpSoftware?.AFFECTED_SOFTWARE_WITH_FLASHING}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, HELP_SUFFIX_WITH_FLASHING, newValue)}
            />
          </AccordionPanel>
          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('software.flashingMBOS')}`}>
            <DoubleTranslatedCustomRichText
              id={ID_FLASHING_MBOS_SOFTWARE}
              title={t('helptext')}
              initialValue={helpSoftware?.AFFECTED_SOFTWARE_FLASHING_MBOS}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, HELP_SUFFIX_FLASHING_MBOS, newValue)}
            />
          </AccordionPanel>
          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('software.codingMBOS')}`}>
            <DoubleTranslatedCustomRichText
              id={ID_CODING_MBOS_SOFTWARE}
              title={t('helptext')}
              initialValue={helpSoftware?.AFFECTED_SOFTWARE_CODING_MBOS}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, HELP_SUFFIX_CODING_MBOS, newValue)}
            />
          </AccordionPanel>
          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('software.OTAUpdateHelp')}`}>
            <DoubleTranslatedCustomRichText
              id={ID_FLASHING_ECU_SOFTWARE_OTA}
              title={t('helptext')}
              initialValue={helpSoftware?.AFFECTED_SOFTWARE_FLASHING_REMOTE_SOFTWARE_UPDATE}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, HELP_SUFFIX_FLASHING_REMOTE_SOFTWARE_UPDATE, newValue)}
            />
          </AccordionPanel>
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  helpSoftware: getHelpSoftware(state),
});

const actionCreators = {
  setSoftware: setHelpSoftware,
};

export default connect(mapStateToProps, actionCreators)(SoftwareHelpText);
